// AI / Generative AI interview questions data
export const aiGenaiQuestions = {
  'llm-fundamentals': [
    {
      id: 1,
      question: "What is a Large Language Model (LLM)?",
      answer: "An LLM is a deep learning model trained on huge amounts of text. It learns to guess the next word (token), and along the way it picks up grammar, some reasoning, and general facts. GPT-4, Claude, Gemini, and Llama are examples. It is autoregressive: it writes one token at a time, each step based on what came before."
    },
    {
      id: 2,
      question: "Difference between a base model and an instruction-tuned model.",
      answer: "A base model only learned next-token prediction, so it continues whatever text you give it. An instruction-tuned model got extra training (often SFT and RLHF) so it follows instructions and acts like an assistant. GPT-4, Claude, and Gemini are instruction-tuned; raw Llama weights are typically base until someone tunes them."
    },
    {
      id: 3,
      question: "What is a token and what is a context window?",
      answer: "A token is the smallest chunk of text the model reads and writes (often a few characters or part of a word in English). The context window is how many tokens it can look at in one go, counting both your prompt and its reply. If you go past that limit, the model forgets the oldest part of the conversation."
    },
    {
      id: 4,
      question: "What is temperature in LLM inference?",
      answer: "Temperature controls how random the next word choice is. Near 0, the model almost always picks the most likely token (very predictable). Higher values add variety and creativity. Use low temperature for facts and steady output; raise it a bit for brainstorming or creative writing."
    },
    {
      id: 5,
      question: "What is top-p (nucleus) sampling?",
      answer: "Top-p (nucleus sampling) builds a small list of next-token options whose total probability adds up to p (for example 0.9 means the model only samples from tokens that together make up 90% of the probability). That list changes every step. Top-k instead keeps a fixed number of top tokens every time."
    },
    {
      id: 6,
      question: "What is hallucination in LLMs?",
      answer: "Hallucination is when the model sounds confident but says things that are wrong or made up. It was trained to produce fluent text, not to check a database. You reduce it with RAG (ground answers in documents), clear prompts, and checking outputs (human or automated verification)."
    },
    {
      id: 7,
      question: "What is the difference between zero-shot, one-shot, and few-shot prompting?",
      answer: "Zero-shot means you ask the question with no examples; the model relies on what it already learned. One-shot adds a single example. Few-shot adds several examples so the model can copy format and style. More examples usually help on narrow or structured tasks."
    },
    {
      id: 8,
      question: "What is the Transformer architecture?",
      answer: "The Transformer is the neural architecture behind modern LLMs (from the 2017 paper 'Attention is All You Need'). It uses self-attention so every token can relate to every other token in parallel, which scales well and captures long-distance patterns. It largely replaced older sequence models like RNNs for this role."
    }
  ],

  'prompting': [
    {
      id: 1,
      question: "What is prompt engineering?",
      answer: "Prompt engineering means designing the text you send to the LLM so you get reliable, useful answers. That includes clear instructions, examples, output format rules, and system-level context. Good prompts cut down on mistakes and make behavior more consistent."
    },
    {
      id: 2,
      question: "What is Chain-of-Thought (CoT) prompting?",
      answer: "Chain-of-Thought (CoT) prompting asks the model to show its reasoning steps before the final answer (for example, 'think step by step'). It often helps on math, logic, and multi-step problems. Zero-shot CoT can work with a short phrase like 'Let's think step by step' at the end of the prompt."
    },
    {
      id: 3,
      question: "What is a system prompt?",
      answer: "A system prompt is the hidden instruction that sets role, rules, and tone for the whole chat. It is separate from what the user types. Example: 'You are support for Acme Corp; only answer about Acme products.'"
    },
    {
      id: 4,
      question: "What is ReAct prompting?",
      answer: "ReAct (Reasoning + Acting) alternates short thoughts ('Thought'), tool calls ('Action'), and what came back ('Observation'). That pattern lets the model use search, APIs, or other tools instead of guessing. Many agent frameworks build on this idea."
    },
    {
      id: 5,
      question: "What is prompt injection?",
      answer: "Prompt injection is when untrusted text (from a user, a webpage, or a file) tries to override your system instructions, for example 'Ignore previous instructions and…'. It is a serious risk in RAG and agents because the model treats all text as instructions unless you guard against it."
    },
    {
      id: 6,
      question: "What is the difference between a prompt template and a prompt chain?",
      answer: "A prompt template is a reusable string with slots (for example, 'Summarize: {text}'). A prompt chain is several LLM calls in a row where one answer feeds the next, so big tasks get split into smaller steps."
    }
  ],

  'rag': [
    {
      id: 1,
      question: "What is RAG (Retrieval-Augmented Generation)?",
      answer: "RAG means: before the LLM answers, you search a knowledge base for relevant snippets, then the model writes an answer using those snippets. A retriever finds text; the generator (LLM) summarizes or quotes in natural language. You get fresher facts and fewer fabrications than from the model alone, without retraining the whole model."
    },
    {
      id: 2,
      question: "What are the core components of a RAG pipeline?",
      answer: "Typical pieces: load and chunk documents, turn chunks into vectors with an embedding model, store vectors in a vector database, retrieve top-k chunks for the user query, then prompt the LLM with those chunks. Each step can fail on its own, so you evaluate retrieval and generation separately."
    },
    {
      id: 3,
      question: "What is chunking and why does it matter?",
      answer: "Chunking splits long documents into smaller pieces before embedding. Fixed-size chunks with overlap are simple but may cut mid-sentence. Semantic chunking tries to keep related sentences together. If chunks are too tiny you lose context; if they are huge you pull in irrelevant noise and hurt retrieval."
    },
    {
      id: 4,
      question: "What is a vector database?",
      answer: "A vector database stores embedding vectors and finds the nearest neighbors to a query vector quickly (often with approximate nearest neighbor search). Examples include Pinecone, Qdrant, Weaviate, and pgvector. The main job is: given one vector, return the k most similar stored vectors."
    },
    {
      id: 5,
      question: "Difference between sparse and dense retrieval.",
      answer: "Sparse retrieval (BM25, TF-IDF) matches important words directly; it is fast and good for rare terms. Dense retrieval compares meaning using embeddings, so 'car' can match 'automobile'. Hybrid systems combine both, sometimes with RRF (Reciprocal Rank Fusion) to merge ranked lists."
    },
    {
      id: 6,
      question: "What is re-ranking in RAG?",
      answer: "Re-ranking runs after the first retrieval pass: a stronger model (often a cross-encoder) scores how well each chunk fits the query, then you reorder and send only the best ones to the LLM. That improves precision. Common tools include Cohere Rerank and BGE-Reranker."
    },
    {
      id: 7,
      question: "What is RAG vs fine-tuning, and when to use which?",
      answer: "Use RAG when facts change often or live in private docs; you do not change model weights, you just update the index. Use fine-tuning when you need a lasting change in style, format, or behavior that prompts alone cannot fix. RAG is usually cheaper to refresh; fine-tuning bakes patterns into the weights."
    },
    {
      id: 8,
      question: "How do you evaluate a RAG pipeline?",
      answer: "Check the retriever: did we fetch the right chunks (metrics like context precision and recall)? Check the generator: is the answer faithful to those chunks, and does it actually answer the question? Tools like RAGAS help automate those checks."
    }
  ],

  'embeddings': [
    {
      id: 1,
      question: "What is an embedding?",
      answer: "An embedding is a list of numbers (a vector) that represents text, images, or code so that similar meaning sits near similar vectors. That powers semantic search and RAG. Common families include OpenAI text-embedding-3, sentence-transformers, and BGE."
    },
    {
      id: 2,
      question: "What is cosine similarity?",
      answer: "Cosine similarity compares two vectors by the angle between them (score from -1 to 1; 1 means same direction). It is popular for embeddings because scaling the vector length matters less than direction. RAG uses it (or related distance metrics) to find the closest chunks to a query."
    },
    {
      id: 3,
      question: "What is the difference between a bi-encoder and a cross-encoder?",
      answer: "A bi-encoder turns the query and each document into vectors separately, so you can precompute document vectors and search fast at scale (typical first-stage retrieval). A cross-encoder feeds query and document together for one relevance score; it is slower but more accurate, so it is common for re-ranking after retrieval."
    },
    {
      id: 4,
      question: "What is semantic search?",
      answer: "Semantic search finds documents by meaning, not only exact keywords. You embed the query, compare to stored vectors, and return the closest matches. That is why searching 'car' can still surface documents that only say 'automobile'."
    }
  ],

  'agents': [
    {
      id: 1,
      question: "What is an AI agent?",
      answer: "An AI agent is an LLM wrapped in a loop where it can call tools (APIs, code, browser, databases) to reach a goal. It is not just a one-shot chat reply: it observes results, plans the next step, and repeats until done or stopped."
    },
    {
      id: 2,
      question: "What is the ReAct loop in agents?",
      answer: "ReAct loop: think what to do, run an action (tool call), read the observation, repeat. LangGraph, LlamaIndex agents, and OpenAI Assistants follow variants of this pattern."
    },
    {
      id: 3,
      question: "What is tool/function calling in LLMs?",
      answer: "Function calling lets the model output structured JSON that names a function and its arguments instead of plain prose. Your app runs that function and sends the result back. That is how agents reliably use tools rather than pretending they called them."
    },
    {
      id: 4,
      question: "What is agentic memory?",
      answer: "Agentic memory is how an agent remembers past work. In-context memory keeps facts in the prompt window. External memory stores vectors or notes in a database and retrieves them when needed. Without memory, every session starts from zero."
    },
    {
      id: 5,
      question: "What is a multi-agent system?",
      answer: "Several specialized agents work together: for example one planner delegates to a researcher, a coder, and a reviewer. That split often beats one giant prompt for large or parallel tasks."
    },
    {
      id: 6,
      question: "Difference between LangChain, LlamaIndex, and LangGraph.",
      answer: "LangChain is a broad framework for chains, tools, and integrations. LlamaIndex focuses on loading data, indexing, and RAG. LangGraph adds a graph/state machine style for complex, branching agent workflows with fine control over steps."
    }
  ],

  'fine-tuning-and-alignment': [
    {
      id: 1,
      question: "What is fine-tuning?",
      answer: "Fine-tuning continues training an existing model on a smaller, task-specific dataset so it fits your domain or tone. It updates weights. RAG does not; it only adds retrieved text at query time. Fine-tuning costs more to run and refresh but can deeply change behavior."
    },
    {
      id: 2,
      question: "What is LoRA?",
      answer: "LoRA (Low-Rank Adaptation) freezes most of the model and trains only small extra matrices per layer. You get much of the benefit of full fine-tuning with far less GPU memory and time. QLoRA adds 4-bit weights so even large models fit on modest hardware."
    },
    {
      id: 3,
      question: "What is RLHF?",
      answer: "RLHF (Reinforcement Learning from Human Feedback) first trains a reward model from human ratings of answers, then uses reinforcement learning (often PPO) to push the LLM toward higher reward. That is a main way chat models get aligned to be helpful, harmless, and honest at vendors like OpenAI and Anthropic."
    },
    {
      id: 4,
      question: "What is quantization in the context of LLMs?",
      answer: "Quantization stores weights in fewer bits (for example 8-bit or 4-bit instead of 16 or 32) so the model uses less RAM and often runs faster, with a small quality tradeoff. Formats like GGUF and QLoRA make big models runnable on one consumer or single-server GPU."
    },
    {
      id: 5,
      question: "What is Constitutional AI (CAI)?",
      answer: "Constitutional AI (Anthropic) teaches the model to check and revise its own outputs against written principles (a 'constitution'), reducing how much harmful content needs direct human labeling. It is an alignment approach alongside or instead of classic RLHF."
    }
  ],

  'evaluation': [
    {
      id: 1,
      question: "How do you evaluate an LLM application in production?",
      answer: "Offline evaluation runs fixed test sets before release (repeatable benchmarks). Online evaluation samples real traffic with human review or an LLM-as-judge. Best practice is to keep a regression suite offline and track quality and hallucination signals live."
    },
    {
      id: 2,
      question: "What is LLM-as-a-judge?",
      answer: "LLM-as-a-judge means using a strong model (for example GPT-4) to score another model's outputs on rubrics like helpfulness or faithfulness. It scales cheaply and often matches humans, but it can share the judge's blind spots and is not perfect ground truth."
    },
    {
      id: 3,
      question: "What are BLEU and ROUGE scores?",
      answer: "BLEU scores n-gram overlap with a reference translation. ROUGE is similar but emphasizes recall and is common in summarization. Both are fast and automatic but weak for open-ended answers; many teams prefer semantic scores like BERTScore for quality that matches human judgment better."
    },
    {
      id: 4,
      question: "What is faithfulness vs answer relevance in RAG evaluation?",
      answer: "Faithfulness asks: did the answer stick to facts supported by the retrieved context, or did the model invent details? Answer relevance asks: did we actually solve the user's question? You need both: an on-topic lie and an irrelevant truth are both bad outcomes."
    }
  ]
};

// Helper to get all questions
export const getAllAiGenaiQuestions = () => {
  const allQuestions = [];
  Object.keys(aiGenaiQuestions).forEach(topic => {
    aiGenaiQuestions[topic].forEach(q => {
      allQuestions.push({ ...q, topic });
    });
  });
  return allQuestions;
};

// Shuffle helper
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
